import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    type CategoryBreakdownDto,
    type ProductsResponseDto,
    type SavingsInsightsSummaryDto,
    getReportSavingsLastRequestParams,
    setSavingsInsightsLastRequestParams,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';

interface UseReportsPageSavingsSummaryPropertyResult {
    isSavingsSummaryVisible: boolean | undefined;
    propertyId: string | undefined;
    propertyName: string;
    loading: boolean;
    summary: SavingsInsightsSummaryDto | null;
    byCategory: CategoryBreakdownDto[];
    products: ProductsResponseDto | null;
    productsCount: number;
    totalSaved: number;
    savingsPerUnit: number | null;
    orderCount: number;
    savingsPerOrder: number | null;
    dateFrom: Date;
    dateTo: Date;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

interface SavingsInsightsRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
}

const buildRequestParamsKey = (params: {
    datePreset?: string;
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
}): string =>
    JSON.stringify({
        datePreset: params.datePreset,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        propertyIds: params.propertyIds,
        categoryIds: params.categoryIds,
    });

const toNumber = (value: string | number | undefined): number => {
    if (typeof value === 'number') {
        return Number.isNaN(value) ? 0 : value;
    }

    const parsed = parseFloat(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
};

const getSavedProductsCount = (
    products: ProductsResponseDto | null,
): number => {
    if (!products?.items?.length) {
        return 0;
    }

    return products.items.filter((item) => toNumber(item.totalSaved) > 0)
        .length;
};

export const useReportsPageSavingsSummaryProperty =
    (): UseReportsPageSavingsSummaryPropertyResult => {
        const dispatch = useAppDispatch();
        const { propertyId } = useParams<{ propertyId: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const lastRequestParams = useSelector(
            getReportSavingsLastRequestParams,
        );
        const properties = useSelector(getPropertiesList);

        const locationState = location.state as {
            propertyName?: string;
        } | null;
        const propertyNameFromState = locationState?.propertyName;

        const [loading, setLoading] = useState(false);
        const [summary, setSummary] =
            useState<SavingsInsightsSummaryDto | null>(null);
        const [byCategory, setByCategory] = useState<CategoryBreakdownDto[]>(
            [],
        );
        const [products, setProducts] = useState<ProductsResponseDto | null>(
            null,
        );

        const requestBody = useMemo<SavingsInsightsRequestBody | null>(() => {
            if (!lastRequestParams || !propertyId) {
                return null;
            }

            return {
                dateFrom: lastRequestParams.dateFrom,
                dateTo: lastRequestParams.dateTo,
                propertyIds: [propertyId],
                ...(lastRequestParams.categoryIds?.length
                    ? {
                          categoryIds: lastRequestParams.categoryIds,
                      }
                    : {}),
            };
        }, [lastRequestParams, propertyId]);

        const targetLastRequestParams = useMemo(() => {
            if (!requestBody || !lastRequestParams) {
                return null;
            }

            return {
                datePreset: lastRequestParams.datePreset,
                dateFrom: requestBody.dateFrom,
                dateTo: requestBody.dateTo,
                propertyIds: requestBody.propertyIds,
                categoryIds: requestBody.categoryIds,
            };
        }, [requestBody, lastRequestParams]);

        const currentLastRequestParamsKey = useMemo(() => {
            if (!lastRequestParams) {
                return null;
            }

            return buildRequestParamsKey(lastRequestParams);
        }, [lastRequestParams]);

        const targetLastRequestParamsKey = useMemo(() => {
            if (!targetLastRequestParams) {
                return null;
            }

            return buildRequestParamsKey(targetLastRequestParams);
        }, [targetLastRequestParams]);

        useEffect(() => {
            if (!targetLastRequestParams || !targetLastRequestParamsKey) {
                return;
            }

            if (currentLastRequestParamsKey === targetLastRequestParamsKey) {
                return;
            }

            dispatch(
                setSavingsInsightsLastRequestParams(targetLastRequestParams),
            );
        }, [
            dispatch,
            currentLastRequestParamsKey,
            targetLastRequestParams,
            targetLastRequestParamsKey,
        ]);

        useEffect(() => {
            if (!requestBody) {
                return;
            }

            let cancelled = false;

            const load = async () => {
                setLoading(true);

                try {
                    const [
                        summaryResponse,
                        byCategoryResponse,
                        productsResponse,
                    ] = await Promise.all([
                        $api.post<SavingsInsightsSummaryDto>(
                            '/v1/reports/savings-insights/summary',
                            requestBody,
                        ),
                        $api.post<{ items: CategoryBreakdownDto[] }>(
                            '/v1/reports/savings-insights/by-category',
                            requestBody,
                        ),
                        $api.post<ProductsResponseDto>(
                            '/v1/reports/savings-insights/products',
                            {
                                ...requestBody,
                                orderBy: '-total_saved',
                                page: 1,
                                pageSize: 25,
                            },
                        ),
                    ]);

                    if (cancelled) {
                        return;
                    }

                    setSummary(summaryResponse.data);
                    setByCategory(byCategoryResponse.data.items ?? []);
                    setProducts(productsResponse.data);
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            };

            void load();

            return () => {
                cancelled = true;
            };
        }, [requestBody]);

        const propertyName = useMemo(() => {
            if (propertyNameFromState) {
                return propertyNameFromState;
            }

            const fromSummary = summary?.byPropertiesData?.find(
                (item) => item.propertyId === propertyId,
            )?.propertyName;
            if (fromSummary) {
                return fromSummary;
            }

            const fromPropertiesList = properties?.find(
                (item) => item.id === propertyId,
            );
            const fromPropertiesListName =
                fromPropertiesList?.propertyNameMarketing ||
                fromPropertiesList?.propertyNameLegal;
            if (fromPropertiesListName) {
                return fromPropertiesListName;
            }

            return propertyId ?? '';
        }, [propertyId, propertyNameFromState, properties, summary]);

        const unitCount = useMemo(() => {
            if (!propertyId) {
                return null;
            }

            const property = properties?.find((item) => item.id === propertyId);
            if (!property || typeof property.unitCount !== 'number') {
                return null;
            }

            return property.unitCount;
        }, [properties, propertyId]);

        const totalSaved = useMemo(
            () => toNumber(summary?.savingsAmount),
            [summary?.savingsAmount],
        );
        const productsCount = useMemo(
            () => getSavedProductsCount(products),
            [products],
        );
        const orderCount = summary?.orderCount ?? 0;

        const savingsPerUnit =
            unitCount != null && unitCount > 0 ? totalSaved / unitCount : null;
        const savingsPerOrder = orderCount > 0 ? totalSaved / orderCount : null;

        const dateFrom = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateFrom)
                    : new Date(),
            [lastRequestParams],
        );
        const dateTo = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateTo)
                    : new Date(),
            [lastRequestParams],
        );

        return {
            isSavingsSummaryVisible,
            propertyId,
            propertyName,
            loading,
            summary,
            byCategory,
            products,
            productsCount,
            totalSaved,
            savingsPerUnit,
            orderCount,
            savingsPerOrder,
            dateFrom,
            dateTo,
            datePreset: lastRequestParams?.datePreset,
            lastRequestParamsPresent: Boolean(lastRequestParams),
        };
    };
